<script>
  import { onMount } from 'svelte';
  import { page } from '$stores/pageStore';
  import { navigate as goto } from 'svelte-routing';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faPlus, faEdit, faTrash, faToggleOn, faToggleOff, faEllipsis, faTrashCan, faPencil, faSquarePlus, faRotate, faCopy, faChevronDown, faChevronUp } from '@fortawesome/pro-light-svg-icons';
  import StudyHeader from '$lib/StudyHeader.svelte';
  import StudyDetails from '$lib/StudyDetails.svelte';
  import FeatureInfo from '$lib/FeatureInfo.svelte';
  import Toolbar from '$lib/Toolbar.svelte';
  import Pagination from '$lib/Pagination.svelte';
  import Footer from '$lib/Footer.svelte';
  import { describeCronExpression } from '$utils/cronDescription.js';
  import { formatToLocalDateTime } from '$utils/dateUtils.js';
  import SchedulerJobForm from './schedulerJobs/SchedulerJobForm.svelte';
  import { selectedStudy } from '$stores/selectionStore';
  import CloneSchedulerJobConfigsModal from './schedulerJobs/CloneSchedulerJobConfigsModal.svelte';

  const API_BASE_URL = '/api/proxy';

  let jobs = [];
  let availableJobIds = [];
  let loading = true;
  let error = null;
  let totalPages = 1;
  let currentPage = 1;
  let pageSize = 20;
  let totalJobs = 0;
  let sortField = 'jobId';
  let sortOrder = 'asc';
  let enabled = '';
  let runtimeStatus = null;
  let runtimeLoading = false;
  let runtimeActionLoading = false;
  let showRuntimeStatus = false;

  // Modal state
  let showForm = false;
  let editJob = null;
  let showCloneModal = false;

  let showMenu = false;
  let menuJob = null;
  const jobsSupportingIgnoreDailyHour = new Set(['form-compliance-check', 'compliance-check']);

  function normalizeVariantName(value) {
    return (value ?? '').toString().trim() || 'default';
  }

  function buildStudyVariantQuery(variantName) {
    return new URLSearchParams({
      studyGUID: $selectedStudy._id,
      variantName: normalizeVariantName(variantName)
    }).toString();
  }

  onMount(() => {
    const urlParams = $page.url.searchParams;
    
    const urlEnabled = urlParams.get('enabled');
    const urlPage = urlParams.get('page');
    const urlSortField = urlParams.get('sortField');
    const urlSortOrder = urlParams.get('sortOrder');
    
    if (urlEnabled !== null) {
      enabled = urlEnabled;
    }
    
    if (urlSortField) {
      sortField = urlSortField;
    }
    
    if (urlSortOrder) {
      sortOrder = urlSortOrder;
    }
    
    const parsedPage = parseInt(urlPage);
    if (!isNaN(parsedPage) && parsedPage > 0) {
      currentPage = parsedPage;
    }
    
    fetchAvailableJobIds();
    fetchRuntimeStatus();
    fetchJobs();

    // Add document event listener for menu closing
    const handleDocumentClick = (event) => {
      if (showMenu && !event.target.closest('.menu-container')) {
        handleMenuClose();
      }
    };

    document.addEventListener('click', handleDocumentClick);

    // Cleanup function
    return () => {
      document.removeEventListener('click', handleDocumentClick);
    };
  });

  async function fetchAvailableJobIds() {
    try {
      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs/availableJobIds`);
      if (!response.ok) {
        throw new Error(`Failed to fetch available job ids: ${response.status} ${response.statusText}`);
      }
      const data = await response.json();
      availableJobIds = Array.isArray(data?.data) ? data.data : [];
    } catch (e) {
      console.error('Error fetching available job ids:', e);
      availableJobIds = [];
    }
  }

  async function fetchJobs() {
    loading = true;
    error = null;
    try {
      if (!$selectedStudy?._id) {
        jobs = [];
        totalPages = 1;
        totalJobs = 0;
        throw new Error('Please select a study to manage scheduler job configs.');
      }

      const queryParams = new URLSearchParams({
        page: currentPage,
        pageSize,
        sortField,
        sortOrder,
        studyGUID: $selectedStudy._id,
        ...(enabled !== '' && { enabled })
      });

      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs?${queryParams}`);
      
      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      
      if (!data || !Array.isArray(data.data)) {
        console.error('Invalid data structure:', data);
        throw new Error('Invalid data structure in response');
      }
      
      jobs = data.data;
      jobs = jobs.map((j) => ({ ...j, variantName: normalizeVariantName(j?.variantName) }));
      totalPages = data.pagination?.totalPages || 1;
      totalJobs = data.pagination?.total || 0;
    } catch (e) {
      error = e.message;
      console.error('Error fetching jobs:', e);
      jobs = [];
    } finally {
      loading = false;
    }
  }

  function updateUrl() {
    const url = new URL(window.location);
    
    if (enabled !== '') {
      url.searchParams.set('enabled', enabled);
    } else {
      url.searchParams.delete('enabled');
    }
    
    url.searchParams.set('page', currentPage.toString());
    url.searchParams.set('sortField', sortField);
    url.searchParams.set('sortOrder', sortOrder);
    
    goto(url.toString(), { replaceState: true });
  }

  function handlePageChange(newPage) {
    currentPage = newPage;
    updateUrl();
    fetchJobs();
  }

  function handleSort(field) {
    if (sortField === field) {
      sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
    } else {
      sortField = field;
      sortOrder = 'asc';
    }
    updateUrl();
    fetchJobs();
  }

  function handleFilter() {
    currentPage = 1;
    updateUrl();
    fetchJobs();
  }

  function handleReset() {
    enabled = '';
    sortField = 'jobId';
    sortOrder = 'asc';
    currentPage = 1;
    updateUrl();
    fetchJobs();
  }

  function handleRefresh() {
    fetchRuntimeStatus();
    fetchJobs();
  }

  function toggleRuntimeStatus() {
    showRuntimeStatus = !showRuntimeStatus;
    if (showRuntimeStatus) {
      fetchRuntimeStatus();
    }
  }

  async function fetchRuntimeStatus() {
    runtimeLoading = true;
    try {
      const response = await fetch(`${API_BASE_URL}/schedulerRuntime`);
      if (!response.ok) {
        throw new Error(`Failed to fetch scheduler runtime status: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();
      runtimeStatus = data?.data || null;
    } catch (e) {
      console.error('Error fetching scheduler runtime status:', e);
      runtimeStatus = null;
    } finally {
      runtimeLoading = false;
    }
  }

  function formatRuntimeDate(value) {
    return value ? formatToLocalDateTime(value) : 'Never';
  }

  async function runRuntimeAction(action, defaultReason) {
    runtimeActionLoading = true;
    error = null;
    try {
      const inputReason = window.prompt('Optional reason for this scheduler runtime action:', defaultReason);
      if (inputReason === null) {
        return;
      }
      const reason = inputReason.trim() || defaultReason;

      const response = await fetch(`${API_BASE_URL}/schedulerRuntime/${action}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ reason })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.message || `Failed to ${action} scheduler runtime`);
      }

      runtimeStatus = data?.data || runtimeStatus;
      await Promise.all([fetchRuntimeStatus(), fetchJobs()]);
    } catch (e) {
      error = e.message;
      console.error(`Error performing scheduler runtime ${action}:`, e);
    } finally {
      runtimeActionLoading = false;
    }
  }

  function openCloneModal() {
    showCloneModal = true;
  }

  function openCreateModal() {
    editJob = null;
    showForm = true;
  }

  function openEditModal(job) {
    editJob = job;
    showForm = true;
  }

  async function handleSubmit(event) {
    const job = event.detail;
    try {
      if (!$selectedStudy?._id) {
        throw new Error('Please select a study before creating/updating a scheduler job config.');
      }

      const variantName = normalizeVariantName(job?.variantName);

      const url = editJob 
        ? `${API_BASE_URL}/schedulerJobConfigs/${job.jobId}?${buildStudyVariantQuery(variantName)}`
        : `${API_BASE_URL}/schedulerJobConfigs`;
      
      const method = editJob ? 'PUT' : 'POST';
      
      // Ensure parameters is an object
      const jobToSubmit = {
        ...job,
        variantName,
        studyGUID: $selectedStudy._id,
        parameters: typeof job.parameters === 'string' 
          ? JSON.parse(job.parameters) 
          : job.parameters
      };
      
      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(jobToSubmit)
      });

      if (!response.ok) {
        throw new Error(`Failed to ${editJob ? 'update' : 'create'} job: ${response.status} ${response.statusText}`);
      }

      showForm = false;
      await Promise.all([fetchJobs(), fetchRuntimeStatus()]);
    } catch (e) {
      error = e.message;
      console.error(`Error ${editJob ? 'updating' : 'creating'} job:`, e);
    }
  }

  async function handleDelete(jobId, variantName) {
    if (!confirm('Are you sure you want to delete this job configuration?')) {
      return;
    }

    try {
      if (!$selectedStudy?._id) {
        throw new Error('Please select a study before deleting a scheduler job config.');
      }

      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs/${jobId}?${buildStudyVariantQuery(variantName)}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        throw new Error(`Failed to delete job: ${response.status} ${response.statusText}`);
      }

      await Promise.all([fetchJobs(), fetchRuntimeStatus()]);
    } catch (e) {
      error = e.message;
      console.error('Error deleting job:', e);
    }
  }

  async function handleToggleEnabled(job) {
    try {
      if (!$selectedStudy?._id) {
        throw new Error('Please select a study before updating a scheduler job config.');
      }

      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs/${job.jobId}?${buildStudyVariantQuery(job.variantName)}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          ...job,
          studyGUID: $selectedStudy._id,
          variantName: normalizeVariantName(job.variantName),
          enabled: !job.enabled
        })
      });

      if (!response.ok) {
        throw new Error(`Failed to update job: ${response.status} ${response.statusText}`);
      }

      await Promise.all([fetchJobs(), fetchRuntimeStatus()]);
    } catch (e) {
      error = e.message;
      console.error('Error updating job:', e);
    }
  }

  function handleMenuClick(job, event) {
    event.stopPropagation();
    const sameRow = menuJob?.jobId === job.jobId && menuJob?.variantName === job.variantName;
    menuJob = job;
    showMenu = sameRow ? !showMenu : true;
  }

  function handleMenuClose() {
    showMenu = false;
    menuJob = null;
  }

  async function executeNow(jobId, variantName, { ignoreDailyHour } = { ignoreDailyHour: false }) {
    try {
      if (!$selectedStudy?._id) {
        throw new Error('Please select a study before running a scheduler job.');
      }

      const response = await fetch(`${API_BASE_URL}/schedulerJobConfigs/${jobId}/run?${buildStudyVariantQuery(variantName)}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ignoreDailyHour })
      });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.message || `Failed to execute job: ${response.status} ${response.statusText}`);
      }
      await Promise.all([fetchJobs(), fetchRuntimeStatus()]);
    } catch (e) {
      error = e.message;
      console.error('Error executing job now:', e);
    }
  }

</script>

<SchedulerJobForm
  {showForm}
  {editJob}
  {availableJobIds}
  on:submit={handleSubmit}
  on:close={() => (showForm = false)}
/>

<CloneSchedulerJobConfigsModal
  bind:show={showCloneModal}
  targetStudy={$selectedStudy}
  on:cloned={() => {
    fetchJobs();
    fetchRuntimeStatus();
  }}
/>

<StudyHeader />

<FeatureInfo 
  title="Job Scheduler" 
  description="Manage and configure automated jobs. Create, edit, enable, or disable jobs and set their execution schedules."
  marginBottom="mb-0"
/>

<StudyDetails showOptionsMenu={false} />

<!-- Title and Filters -->
<Toolbar title="Job Scheduler" itemCountTitle="Jobs" itemCount={totalJobs}>
  <div class="flex items-center gap-4">
    <div class="text-m mb-0">
      Status
      <select
        bind:value={enabled}
        on:change={handleFilter}
        class="h-6 w-32 ms-1 px-1 border rounded dark:bg-gray-700 dark:border-gray-600 text-sm text-gray-800 dark:text-gray-100"
      >
        <option value="">All</option>
        <option value="true">Enabled</option>
        <option value="false">Disabled</option>
      </select>
    </div>

    <button
      on:click={openCreateModal}
      class="pagination mx-1 p-0"
      title="Add new job"
    >
      <FontAwesomeIcon icon={faSquarePlus} />
    </button>
    <button
      on:click={openCloneModal}
      class="pagination mx-1 p-0"
      title="Clone scheduler job configs from another study (overwrite on conflict)"
    >
      <FontAwesomeIcon icon={faCopy} />
    </button>
    <button on:click={handleRefresh} class="pagination mx-1 p-0" title="Refresh">
      <FontAwesomeIcon icon={faRotate} />
    </button>
    <button
      on:click={toggleRuntimeStatus}
      class="pagination mx-1 p-0"
      title={showRuntimeStatus ? 'Hide runtime status' : 'Show runtime status'}
    >
      <FontAwesomeIcon icon={showRuntimeStatus ? faChevronUp : faChevronDown} />
    </button>
  </div>
</Toolbar>

{#if showRuntimeStatus}
  <div class="mx-1 mb-0 rounded border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
    <div class="flex flex-wrap items-start justify-between gap-4">
      <div>
        <div class="text-sm font-semibold text-gray-900 dark:text-gray-100">Runtime Status</div>
        {#if runtimeLoading && !runtimeStatus}
          <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">Loading scheduler runtime status...</div>
        {:else if runtimeStatus}
          <div class="mt-2 grid grid-cols-1 gap-2 text-sm text-gray-800 dark:text-gray-100 md:grid-cols-2 xl:grid-cols-3">
            <div><span class="font-medium">Local state:</span> {runtimeStatus.localSchedulingState || (runtimeStatus.effectiveEnabled ? 'running' : 'stopped')}</div>
            <div><span class="font-medium">Shared DB state:</span> {runtimeStatus.runtimeEnabled ? 'Enabled' : 'Disabled'}</div>
            <div><span class="font-medium">Env disabled:</span> {runtimeStatus.envDisabled ? 'Yes' : 'No'}</div>
            <div><span class="font-medium">Current owner:</span> {runtimeStatus.runtimeState?.ownerInstanceId || 'None'}</div>
            <div><span class="font-medium">Owner host:</span> {runtimeStatus.runtimeState?.ownerHostname || 'Unknown'}</div>
            <div><span class="font-medium">Owner active:</span> {runtimeStatus.ownerActive ? 'Yes' : 'No'}</div>
            <div><span class="font-medium">This instance owns scheduler:</span> {runtimeStatus.isCurrentOwner ? 'Yes' : 'No'}</div>
            <div><span class="font-medium">Environment:</span> {runtimeStatus.environment || 'Unknown'}</div>
            <div><span class="font-medium">Instance:</span> {runtimeStatus.instanceId || 'Unknown'}</div>
            <div><span class="font-medium">Hostname:</span> {runtimeStatus.hostname || 'Unknown'}</div>
            <div><span class="font-medium">Process ID:</span> {runtimeStatus.pid || 'Unknown'}</div>
            <div><span class="font-medium">Database:</span> {runtimeStatus.dbName || 'Unknown'}</div>
            <div><span class="font-medium">Active jobs:</span> {runtimeStatus.activeJobCount ?? 0}</div>
            <div><span class="font-medium">Lease acquired:</span> {formatRuntimeDate(runtimeStatus.runtimeState?.lockAcquiredAt)}</div>
            <div><span class="font-medium">Lease heartbeat:</span> {formatRuntimeDate(runtimeStatus.runtimeState?.lockHeartbeatAt)}</div>
            <div><span class="font-medium">Lease expires:</span> {formatRuntimeDate(runtimeStatus.runtimeState?.lockExpiresAt)}</div>
            <div><span class="font-medium">Last state change:</span> {formatRuntimeDate(runtimeStatus.runtimeState?.updatedAt)}</div>
            <div><span class="font-medium">Changed by:</span> {runtimeStatus.runtimeState?.updatedBy || 'Unknown'}</div>
            <div><span class="font-medium">Reason:</span> {runtimeStatus.effectiveDisableReason || runtimeStatus.runtimeState?.reason || 'None recorded'}</div>
            <div><span class="font-medium">Last resync:</span> {formatRuntimeDate(runtimeStatus.runtimeState?.lastResyncRequestedAt)}</div>
            <div><span class="font-medium">Resync requested by:</span> {runtimeStatus.runtimeState?.lastResyncRequestedBy || 'Unknown'}</div>
            <div><span class="font-medium">Resync reason:</span> {runtimeStatus.runtimeState?.lastResyncReason || 'None recorded'}</div>
          </div>
        {:else}
          <div class="mt-2 text-sm text-gray-500 dark:text-gray-400">Scheduler runtime status is unavailable.</div>
        {/if}
      </div>

      <div class="flex flex-wrap items-center gap-2">
        <button
          on:click={() => runRuntimeAction('start', 'Scheduler runtime started from Job Scheduler UI')}
          class="h-8 rounded border px-3 text-sm text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          disabled={runtimeActionLoading || (runtimeStatus?.envDisabled ?? false)}
          title={runtimeStatus?.envDisabled ? 'This instance is force-disabled by DISABLE_SCHEDULED_JOBS=true' : 'Enable shared scheduler runtime and let one instance acquire the scheduler lease'}
        >
          Start / resync
        </button>
        <button
          on:click={() => runRuntimeAction('stop', 'Scheduler runtime stopped from Job Scheduler UI')}
          class="h-8 rounded border px-3 text-sm text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          disabled={runtimeActionLoading}
          title="Disable shared scheduler runtime and release the scheduler lease"
        >
          Stop now
        </button>
        <button
          on:click={() => runRuntimeAction('resync', 'Scheduler runtime resync requested from Job Scheduler UI')}
          class="h-8 rounded border px-3 text-sm text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          disabled={runtimeActionLoading || (runtimeStatus && !runtimeStatus.runtimeEnabled)}
          title="Request a scheduler resync across instances using the shared DB state"
        >
          Request resync
        </button>
        <button
          on:click={fetchRuntimeStatus}
          class="h-8 rounded border px-3 text-sm text-gray-800 hover:bg-gray-100 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 dark:hover:bg-gray-600"
          disabled={runtimeLoading || runtimeActionLoading}
        >
          Refresh status
        </button>
      </div>
    </div>
  </div>
{/if}

<!-- Error Message -->
{#if error}
  <div class="text-red-500 text-center p-4">{error}</div>
{/if}

<!-- Jobs Table -->
{#if loading}
  <div class="flex justify-center items-center h-64">
    <div class="loader"></div>
  </div>
{:else}
  <div class="px-1 pt-0">
    <table class="min-w-full border-collapse table-auto">
      <thead>
        <tr class="text-sm text-black dark:text-slate-300 bg-stone-200/50 dark:bg-gray-800/90">
          <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('jobId')}>
            Job ID
            {#if sortField === 'jobId'}
              {sortOrder === 'asc' ? '↑' : '↓'}
            {/if}
          </th>
          <th class="px-4 py-2 text-left bold cursor-pointer" on:click={() => handleSort('variantName')}>
            Variant
            {#if sortField === 'variantName'}
              {sortOrder === 'asc' ? '↑' : '↓'}
            {/if}
          </th>
          <th class="px-4 py-2 text-left bold">Status</th>
          <th class="px-4 py-2 text-left bold">Schedule</th>
          <th class="px-4 py-2 text-left bold">Recipients</th>
          <th class="px-4 py-2 text-left bold">Last Run</th>
          <th class="px-4 py-2 text-left bold">Next Run</th>
          <th class="px-4 py-2 text-left bold w-8">&nbsp;</th>
        </tr>
      </thead>
      <tbody>
        {#each jobs as job, index (job._id || `${job.jobId}::${job.variantName}`)}
          <tr class="text-sm hover:bg-white dark:hover:bg-gray-775 text-gray-800 dark:text-slate-300 {index % 2 === 0 ? 'bg-stone-50 dark:bg-gray-725' : 'bg-stone-75 dark:bg-gray-750'}">
            <td class="px-4 py-2 whitespace-nowrap" title={job.description || 'No description available'}>{job.jobId}</td>
            <td class="px-4 py-2 whitespace-nowrap">{job.variantName}</td>
            <td class="px-4 py-2 whitespace-nowrap">{job.enabled ? 'Enabled' : 'Disabled'}</td>
            <td class="px-4 py-2 whitespace-nowrap">
              <div class="flex flex-col">
                <span class="text-xs text-gray-500 dark:text-gray-400">{job.cronSchedule}</span>
                <span>{describeCronExpression(job.cronSchedule)}</span>
              </div>
            </td>
            <td class="px-4 py-2 whitespace-nowrap capitalize">{job.recipientType || 'participant'}</td>
            <td class="px-4 py-2 whitespace-nowrap">{job.lastRun ? formatToLocalDateTime(job.lastRun) : 'Never'}</td>
            <td class="px-4 py-2 whitespace-nowrap">
              {#if !job.enabled}
                Not scheduled
              {:else if job.nextRun}
                {formatToLocalDateTime(job.nextRun)}
              {:else}
                Not scheduled
              {/if}
            </td>
            <td class="py-2">
              <div class="menu-container relative">
                <button
                  on:click={(e) => handleMenuClick(job, e)}
                  class="pagination mx-1 p-0"
                  title="More options"
                >
                  <FontAwesomeIcon icon={faEllipsis} class="text-sky-800 dark:text-white/70" />
                </button>
                {#if showMenu && menuJob?.jobId === job.jobId && menuJob?.variantName === job.variantName}
                  <div class="absolute right-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-md shadow-lg py-1 z-50">
                    <button
                      on:click={() => {
                        openEditModal(job);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>Edit job</span>
                      <FontAwesomeIcon icon={faPencil} class="text-sky-800 dark:text-white/70" />
                    </button>
                    <div class="px-4 py-2 border-t border-gray-200 dark:border-gray-700">
                      <div class="text-xs text-gray-500 dark:text-gray-400 mb-1">Execute now</div>
                      <div class="flex items-center justify-between gap-2">
                        <button
                          on:click={() => {
                            executeNow(job.jobId, job.variantName, { ignoreDailyHour: false });
                            handleMenuClose();
                          }}
                          class="text-left px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                          title="Execute job immediately"
                        >
                          Run now
                        </button>
                        {#if jobsSupportingIgnoreDailyHour.has(job.jobId)}
                          <button
                            on:click={() => {
                              executeNow(job.jobId, job.variantName, { ignoreDailyHour: true });
                              handleMenuClose();
                            }}
                            class="text-left px-2 py-1 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
                            title="Run now and ignore configured send hours"
                          >
                            Run now (ignore send hours)
                          </button>
                        {/if}
                      </div>
                    </div>
                    <button
                      on:click={() => {
                        handleDelete(job.jobId, job.variantName);
                        handleMenuClose();
                      }}
                      class="w-full text-left px-4 py-2 text-sm text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center justify-between"
                    >
                      <span>Delete job</span>
                      <FontAwesomeIcon icon={faTrashCan} class="text-sky-800 dark:text-white/70" />
                    </button>
                  </div>
                {/if}
              </div>
            </td>
          </tr>
        {/each}
      </tbody>
    </table>
  </div>
{/if}

<Footer>
  <Pagination {currentPage} {totalPages} onPageChange={handlePageChange} />
</Footer> 